'use client';

import React, { useState } from 'react';
import { Tag, CheckCircle, X, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

interface CouponInputProps {
  onApply: (discount: number, code: string) => void;
  applied: boolean;
  bookingAmount: number;
}

export default function CouponInput({ onApply, applied, bookingAmount }: CouponInputProps) {
  const { user } = useAuth();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [appliedCode, setAppliedCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(0);
  const [loading, setLoading] = useState(false);

  const handleApply = async () => {
    const upper = code.trim().toUpperCase();
    if (!upper) { setError('Please enter a coupon code'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/coupon/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: upper,
          booking_amount: bookingAmount,
          user_id: user ? (user as { id: string }).id : null,
        }),
      });
      const data = await res.json();
      if (data.valid) {
        setAppliedCode(upper);
        setAppliedDiscount(data.discount);
        onApply(data.discount, upper);
      } else {
        setError(data.message || 'Invalid coupon code');
      }
    } catch {
      setError('Failed to validate coupon. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = () => {
    setCode('');
    setAppliedCode('');
    setAppliedDiscount(0);
    setError('');
    onApply(0, '');
  };

  return (
    <div className="bg-white rounded-2xl border border-border shadow-sm p-5">
      <h3 className="font-bold text-foreground text-base mb-1 flex items-center gap-2">
        <Tag size={16} className="text-primary" />
        Apply Coupon
      </h3>
      <p className="text-xs text-muted-foreground mb-4">
        Have a promo code? Enter it below to get a discount.
      </p>

      {applied && appliedCode ? (
        <div className="flex items-center justify-between bg-secondary-light border border-secondary/30 rounded-xl px-4 py-3">
          <div className="flex items-center gap-2">
            <CheckCircle size={16} className="text-secondary" />
            <span className="font-bold text-secondary text-sm">{appliedCode} applied!</span>
            <span className="text-xs text-secondary">You saved ₹{appliedDiscount?.toLocaleString('en-IN')}</span>
          </div>
          <button type="button" onClick={handleRemove} className="text-muted-foreground hover:text-red-500 transition-colors p-1" aria-label="Remove coupon">
            <X size={16} />
          </button>
        </div>
      ) : (
        <>
          <div className="flex gap-2">
            <input
              type="text"
              className="input-field flex-1 uppercase"
              placeholder="Enter coupon code"
              value={code}
              onChange={(e) => { setCode(e.target.value.toUpperCase()); setError(''); }}
              onKeyDown={(e) => e.key === 'Enter' && handleApply()}
              disabled={loading}
            />
            <button type="button" onClick={handleApply} disabled={loading} className="btn-primary px-5 py-2.5 text-sm flex-shrink-0 flex items-center gap-1.5">
              {loading ? <Loader2 size={14} className="animate-spin" /> : null}
              {loading ? 'Checking...' : 'Apply'}
            </button>
          </div>
          {error && <p className="text-red-500 text-xs mt-2">{error}</p>}
          <div className="flex flex-wrap gap-2 mt-3">
            {['FIRSTRIDE', 'GHUMOJI10', 'MANIMAHESH15'].map((c) => (
              <button key={`coupon-chip-${c}`} type="button" onClick={() => setCode(c)} className="filter-chip text-xs">
                {c}
              </button>
            ))}
          </div>
          {!user && (
            <p className="text-xs text-muted-foreground mt-2">
              💡 <a href="/login" className="text-primary font-semibold hover:underline">Sign in</a> to use first-ride and returning customer coupons
            </p>
          )}
        </>
      )}
    </div>
  );
}